import { useMutation, useQuery } from '@apollo/client';
import { editProcedure, reactivateProcedure, procedureEditFieldsQuery } from '@/graph/procedures';
import get from 'lodash/fp/get';
import { format } from 'date-fns';
import map from 'lodash/fp/map';
import React, { useMemo, useState } from 'react';
import SubmitAndDangerLinkButtonForm from '@/se/components/forms/SubmitAndDangerLinkButtonForm';
import Modal from '@/se/components/Modal';
import { compose, mapProps, withProps } from 'recompose';
import { withLabel } from '@/se/components/Label';
import { list as listRooms } from '../../../../../graph/rooms';
import flow from 'lodash/fp/flow';
import concat from 'lodash/fp/concat';
import filter from 'lodash/fp/filter';
import uniq from 'lodash/fp/uniq';
import reduce from 'lodash/fp/reduce';
import SelectNumberInput from '@/se/components/inputs/SelectNumberInput';
import SelectInput from '@/se/components/inputs/SelectInput';
import TimeInputControl from '@/se/components/controls/TimeControl';
import NumberInput from '@/se/components/inputs/NumberInput';
import PhysicianSelectInput from '../../../../inputs/physician/PhysicianSelectInput';
import AnesthesiologistSelectInput from '../../../../inputs/anesthesiologist/AnesthesiologistSelectInput';
import TextInput from '@/se/components/inputs/TextInput';
import TextAreaInput from '@/se/components/inputs/TextAreaInput';
import ObjectInput from '@/se/components/inputs/ObjectInput';
import CustomNoteInput from '@/se/components/inputs/CustomNoteInput';
import styled from 'styled-components';
import { MobilePhoneNumberInput } from '../../../../entities/frontProcedures/columns';
import { anesthesiaTypes } from '../anesthesiologist/AnesthesiaType';
import ProcedureCancellationModal from './ProcedureCancellationModal';
import { unparsedAllergiesSummary } from '@/allergies/AllergiesView';
import { CenteredSpinner } from '@/se/components/Spinner';

export const FieldRow = styled.div`
  display: flex;
  margin: 0 -0.5rem;

  > div {
    overflow: hidden;
    flex: 1 0 0;
    margin: 0 0.5rem;
    box-sizing: border-box;
  }
`;

// Replace deprecated withQuery HOC with useQuery hook
const RoomSelectInput = ({ operationRooms, ...props }: any) => {
  const { data } = useQuery(listRooms, { fetchPolicy: 'cache-and-network' });

  const options = useMemo(
    () =>
      flow(
        concat(
          flow(
            get('rooms'),
            filter((room: any) => room.type === 'OR'),
            map(get('name'))
          )(data || {})
        ),
        uniq,
        reduce((acc, operationRoom) => ({ ...acc, [operationRoom]: operationRoom }), {})
      )(operationRooms),
    [data, operationRooms]
  );

  return <SelectInput {...props} options={options} />;
};

const ProcedureInput = withProps({
  schema: {
    operationRoom: compose(
      withLabel('Operation Room'),
      withProps({
        name: 'operationRoom',
        required: true,
        isClearable: false,
      })
    )(RoomSelectInput),
    startTime: withProps({ required: true })(withLabel('Start Time')(TimeInputControl)),
    duration: withProps({ min: 5, max: 1440, required: true })(withLabel('Duration')(NumberInput)),
    physicianId: withProps({ required: true })(PhysicianSelectInput),
    anesthesiologistId: AnesthesiologistSelectInput,
    anesthesiaType: compose(
      withLabel('Anesthesia Type'),
      withProps({
        options: anesthesiaTypes.reduce((r, e) => ({ ...r, [e]: e }), {}),
        placeholder: 'e.g., G',
      })
    )(SelectInput),
    patientName: compose(withLabel('Patient Name'), withProps({ placeholder: 'e.g., Mark Nordstrom' }))(TextInput),
    patientAge: compose(
      withLabel('Age'),
      withProps({ min: 0 }),
      mapProps(({ onChange, ...props }: any) => ({
        onChange: value => {
          if ((value || '').match(/^\d*\s?(m|mo)?$/)) {
            onChange(value);
          }
        },
        onBlur: e => {
          if (!(e.target.value || '').match(/^\d+\s?(m|mo)?$/)) {
            onChange(null);
          }
        },
        ...props,
      }))
    )(TextInput),
    patientSex: compose(withLabel('Sex'), withProps({ maxLength: 1 }))(TextInput),
    patientCellPhone: MobilePhoneNumberInput,
    procedureType: compose(
      withLabel('Procedure Type'),
      withProps({ placeholder: 'e.g., Neuroimaging' })
    )(TextAreaInput),
    notes: withProps({ rows: 4 })(withLabel('Notes')(TextAreaInput)),
    preOpNote: withProps({ rows: 4 })(withLabel('PreOp Note')(TextAreaInput)),
    anesthesiologistNotes: compose(
      withProps({ options: ['Drugs in room', 'MH', 'Latex', 'Glidescope', 'ICD/Magnet', 'Local'] }),
      withLabel('Anesthesiologist Notes')
    )(CustomNoteInput),
    orNotes: compose(
      withProps({ options: ['Case cart by room', '*Local*', 'Combo case', 'Graft in house'] }),
      withLabel('OR Notes')
    )(CustomNoteInput),
    allergies: compose(
      withLabel('Allergies'),
      mapProps((props: any) => {
        const parsedAllergy = unparsedAllergiesSummary(props?.value);
        const isJson = props?.rootValue?.allergies?.[0] === '{';
        const value = parsedAllergy ? parsedAllergy : '';
        return {
          ...props!,
          isDisabled: isJson && parsedAllergy,
          value: value,
        };
      })
    )(TextAreaInput),
    procedurePriority: compose(
      withLabel('Procedure Priority'),
      withProps({
        options: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
        placeholder: 'Select Procedure Priority',
      })
    )(SelectNumberInput),
  },
  render: props => {
    const {
      operationRoom,
      startTime,
      duration,
      physicianId,
      anesthesiologistId,
      anesthesiaType,
      patientName,
      patientAge,
      patientSex,
      patientCellPhone,
      procedureType,
      notes,
      allergies,
      procedurePriority,
      anesthesiologistNotes,
      orNotes,
      preOpNote,
    } = props;

    return (
      <div>
        <FieldRow>
          <div style={{ flex: '0 1 calc(45% - 1rem)' }}>{operationRoom}</div>
        </FieldRow>
        <FieldRow>
          <div style={{ flex: '0 1 calc(30% - 1rem)' }}>{startTime}</div>
          <div style={{ flex: '0 1 calc(30% - 1rem)' }}>{duration}</div>
        </FieldRow>
        <FieldRow>
          <div style={{ flex: '0 1 calc(60% - 1rem)' }}>{physicianId}</div>
        </FieldRow>
        <FieldRow style={{ marginBottom: '2em', marginTop: '2em' }}>
          <div>{anesthesiologistId}</div>
          <div>{anesthesiaType}</div>
        </FieldRow>
        <FieldRow>
          <div style={{ flex: '0 1 calc(60% - 1rem)' }}>{patientName}</div>
          <div>{patientAge}</div>
          <div>{patientSex}</div>
        </FieldRow>
        <div>{patientCellPhone}</div>
        <div>{procedureType}</div>
        <div>{notes}</div>
        <div>{allergies}</div>
        <div>{preOpNote}</div>
        <div>{anesthesiologistNotes}</div>
        <div>{orNotes}</div>
        <div>{procedurePriority}</div>
      </div>
    );
  },
})(ObjectInput);

const ProcedureEditor = ({ date, operationRooms, procedure, onDone, onClose, fullWindow, isAnesthesiologist }) => {
  // Note: Procedure subscriptions are now handled at the HospitalSchedule level
  // This ensures subscriptions stay active even when the editor closes,
  // providing instant updates without waiting for the full 30s refresh

  const [doUpdateProcedure] = useMutation(editProcedure);

  const [reactivateProcedureMutation] = useMutation(reactivateProcedure);

  const [cancelModal, setCancelModal] = useState<boolean>(false);

  // Phase 2: Lazy load edit fields only when editing
  const procedureId = procedure?.id;
  const {
    data: editFieldsData,
    loading: detailLoading,
    error: detailError,
  } = useQuery(procedureEditFieldsQuery, {
    variables: { id: procedureId },
    skip: !procedureId,
    fetchPolicy: 'cache-and-network',
  });

  // Merge base procedure with lazy-loaded edit fields
  const resolvedProcedure = useMemo(() => {
    if (!procedure) return editFieldsData?.procedureEditFields;
    if (!editFieldsData?.procedureEditFields) return procedure;
    return { ...procedure, ...editFieldsData.procedureEditFields };
  }, [procedure, editFieldsData?.procedureEditFields]);

  const toggleCancelModal = () => {
    setCancelModal(v => !v);
  };

  const onCancelProcedure = () => {
    toggleCancelModal();
    onDone();
  };

  const handleReactivateProcedure = async () => {
    if (window.confirm('Are you sure you want to reactivate this procedure?')) {
      await reactivateProcedureMutation({ variables: { id: resolvedProcedure?.id } });
      onDone();
    }
  };

  const handleSubmit = async ({ ...data }) => {
    try {
      await doUpdateProcedure({
        variables: {
          id: get('id')(resolvedProcedure),
          date: format(date, 'YYYY-MM-DD'),
          ...data,
        },
      });
    } catch (e: any) {
      const validationErrorMessage = (e?.message.match(/^GraphQL error: Validation error: (.*)$/) || [])[1];

      if (validationErrorMessage) {
        throw new Error(validationErrorMessage);
      } else {
        throw e;
      }
    }
    onDone();
  };

  const initialValue = useMemo(
    () => ({
      operationRoom: get('or')(resolvedProcedure),
      startTime: get('startTimeText')(resolvedProcedure),
      duration: get('duration')(resolvedProcedure),
      physicianId: get('physician.id')(resolvedProcedure),
      anesthesiologistId: get('anesthesiologist.id')(resolvedProcedure),
      anesthesiaType: get('anesthesiaType')(resolvedProcedure) || '',
      patientName: get('patient.name')(resolvedProcedure) || get('patientName')(resolvedProcedure) || '',
      patientAge: get('patientAge')(resolvedProcedure),
      patientSex: get('patientSex')(resolvedProcedure) || '',
      patientCellPhone: get('patientCellPhone')(resolvedProcedure) || undefined,
      procedureType: get('procedureType')(resolvedProcedure) || '',
      notes: get('notes')(resolvedProcedure),
      allergies: get('allergies')(resolvedProcedure),
      procedurePriority: get('procedurePriority')(resolvedProcedure),
      color: get('color')(resolvedProcedure),
      anesthesiologistNotes: get('anesthesiologistNotes')(resolvedProcedure),
      orNotes: get('orNotes')(resolvedProcedure),
      preOpNote: get('preOpNote')(resolvedProcedure),
    }),
    [resolvedProcedure]
  );

  if (cancelModal) {
    return <ProcedureCancellationModal procedureId={resolvedProcedure?.id} onDone={onCancelProcedure} />;
  }

  if (procedureId && detailLoading && !resolvedProcedure && !detailError) {
    return (
      <Modal title="Edit Procedure" withRouter={false} onClose={onClose} fullWindow={fullWindow}>
        <CenteredSpinner />
      </Modal>
    );
  }

  const targetProcedure = resolvedProcedure || procedure;

  return (
    <Modal title="Edit Procedure" withRouter={false} onClose={onClose} fullWindow={fullWindow}>
      <SubmitAndDangerLinkButtonForm
        key={targetProcedure?.updatedAt ? `${targetProcedure.id}-${targetProcedure.updatedAt}` : targetProcedure?.id}
        input={ProcedureInput}
        inputProps={{ operationRooms, isAnesthesiologist }}
        initialValue={initialValue}
        label="Save"
        onSubmit={handleSubmit}
        onDangerLinkButtonClick={targetProcedure?.isCanceled ? handleReactivateProcedure : toggleCancelModal}
        onCancel={onClose}
        dangerLabel={targetProcedure?.isCanceled ? 'Reactivate Procedure' : 'Cancel Procedure'}
        disabled={detailLoading && !detailError}
        context={{
          isCreate: false,
          isEdit: true,
        }}
      />
    </Modal>
  );
};

export default ProcedureEditor;
